/**
 * The chat's active context — global, a client, or a case (§6b). Composed by the `web-chat` store
 * facade (MAR-948), which owns `conversationId` and passes it in; this owns everything about the scope
 * itself, including adopting the rung an opened conversation was filed under.
 *
 * Built before anything that reads the scope (the draft flush, the workflow actions, the confirm
 * cards), so each of those receives these refs in its deps rather than closing over a binding
 * declared further down the store.
 */
import { computed, ref, type Ref } from 'vue'
import {
  GLOBAL_SCOPE, toWire, readRememberedScope, writeRememberedScope, decideRememberedScope,
  type ChatScope, type ChatScopeWire,
} from '~~/shared/web-chat/scope'
import { browserStorage } from '~/composables/usePersistedPref'

export interface ChatScopeDeps {
  conversationId: Ref<string | null>
  /** Persists a rung onto an existing conversation row (the chats store). */
  persistScope: (conversationId: string, scope: ChatScopeWire) => Promise<unknown>
  /** Opens the context rail — called only when a USER picks a rung, never on a restore. */
  revealContextRail: () => void
}

export type ScopeCaseItem = { id: string; name: string; clientId?: string | null; clientName?: string | null }

export function useChatScope({ conversationId, persistScope, revealContextRail }: ChatScopeDeps) {
  // **The active context, and the app's source of truth for it** — what the user picked, the
  // remembered rung a new chat was seeded with, or the rung an opened conversation was filed under
  // (`adoptConversationScope`). One rung only, which is why it is a single union ref rather than two
  // independent ids that could contradict.
  const activeScope = ref<ChatScope>(GLOBAL_SCOPE)
  // conversationId → the scope stored on ITS ROW, kept so switching back to an already-loaded chat
  // can adopt its rung without a refetch.
  const scopeCache = new Map<string, ChatScope>()

  // Back-compat readers for one rung. The case hub and ChatPanel's pending-scope capture only care
  // about "is this chat filed under case X"; the draft flush threads both into the run (MAR-448,
  // AlexAmin/marcus#206) — only one is ever non-null, since ChatScope holds one rung.
  const activeCaseId = computed(() => activeScope.value.kind === 'case' ? activeScope.value.id : null)
  const activeCaseName = computed(() => activeScope.value.kind === 'case' ? activeScope.value.name : null)
  const activeClientId = computed(() => activeScope.value.kind === 'client' ? activeScope.value.id : null)

  /**
   * The one place `activeScope` is assigned. The distinction from `setScope` is INTENT: `setScope` is
   * a user choosing a rung (it persists, remembers, and reveals the rail); `adoptScope` is the store
   * catching up with a scope that already applies — yesterday's remembered value, or the rung an
   * opened conversation's row holds — and must do none of those, or restoring a scope would rewrite
   * the remembered one and pop the rail open.
   */
  function adoptScope(scope: ChatScope) {
    activeScope.value = scope
  }

  /**
   * The single write path for a user's choice (§6b): sets the chip, remembers it for the next new
   * chat, reveals the context rail (MAR-128), and — when a conversation already exists — persists the
   * rung to its row. A pending chat's rung rides `createConversation` instead.
   */
  async function setScope(scope: ChatScope) {
    adoptScope(scope)
    rememberScope(scope)
    if (scope.kind !== 'global') revealContextRail()
    const id = conversationId.value
    if (!id) return
    scopeCache.set(id, scope)
    await persistScope(id, toWire(scope))
  }

  /** Record what a conversation's row holds, so switching back to it can offer it without a fetch. */
  function rememberConversationScope(id: string, scope: ChatScope) {
    scopeCache.set(id, scope)
  }

  /**
   * **An opened conversation's saved rung WINS over the selected context, silently** (D3, decided by
   * Alex on 2026-09-29, MAR-990 — reversing f3d7b91f, which kept the selection and turned the row
   * into a banner offering to restore it). A chat filed under case B continues against case B's
   * documents even if the user was working in case A a moment ago; the chip shows B and nothing
   * else announces it. Through `adoptScope`: the store catching up with the row, so nothing is
   * persisted, remembered for the next new chat, or revealed.
   *
   * A conversation with NO saved rung (global) leaves the current selection alone — there is nothing
   * of its own to restore, and dropping the user's case on every unscoped chat would be the old
   * complaint in reverse. That is how "an old conversation with a saved scope" was read; a global
   * row winning too would be a one-line change here.
   */
  function adoptConversationScope(id: string, scope: ChatScope = scopeCache.get(id) ?? GLOBAL_SCOPE) {
    if (scope.kind !== 'global') adoptScope(scope)
  }

  // ── Remembered scope ─────────────────────────────────────────────────────────────────────────
  // Someone working a case all day shouldn't re-pick it for every new chat. A *seed* for pending
  // chats only, stored per TAB (sessionStorage) with localStorage as the cross-session seed — see
  // `readRememberedScope` in shared/web-chat/scope.ts.
  //
  // `knownScopeIds` are the ids the current org can see, published by the scope bar once its case
  // list loads. `null` means NOT KNOWN YET — distinct from an empty Set ("loaded, and there are
  // none"). Only the latter may drop the remembered scope; treating them alike erased it on a failed load.
  const knownScopeIds = ref<Set<string> | null>(null)

  function setKnownScopeIds(ids: Set<string>) {
    knownScopeIds.value = ids
  }

  /** Best-effort — private mode / quota failures must never break the composer. */
  function rememberScope(scope: ChatScope) {
    if (!import.meta.client) return
    try { writeRememberedScope(scope, browserStorage('session'), browserStorage('local')) } catch { /* quota */ }
  }

  function readStoredScope(): ChatScope {
    if (!import.meta.client) return GLOBAL_SCOPE
    try {
      return readRememberedScope(browserStorage('session'), browserStorage('local'))
    } catch {
      return GLOBAL_SCOPE
    }
  }

  /**
   * Seed a pending chat from the remembered scope. No-ops unless there is no conversation row and no
   * scope yet, so it can never override a loaded conversation or a scope the user just picked.
   */
  function applyRememberedScope() {
    if (!import.meta.client) return
    if (conversationId.value || activeScope.value.kind !== 'global') return
    const remembered = readStoredScope()
    // 'wait' = the allow-list isn't known yet: restore nothing and, crucially, FORGET nothing.
    switch (decideRememberedScope(remembered, knownScopeIds.value)) {
      case 'restore': adoptScope(remembered); break
      case 'forget': rememberScope(GLOBAL_SCOPE); break
    }
  }

  /**
   * Drop the scope when the case or client it points at has been deleted (MAR-461) — the only
   * automatic unset there is, because the alternative is a chip over a dead id while the tools
   * filter to nothing. Through `setScope`, so the row and BOTH storage tiers are cleared.
   *
   * Deleting the CLIENT of a case rung does not kill it: `cases.client_id` is `ON DELETE set null`,
   * so the case outlives its client. Demote the rung's client half instead of dropping the rung.
   */
  async function clearScopeForDeleted(ids: Iterable<string>) {
    const scope = activeScope.value
    if (scope.kind === 'global') return
    const gone = ids instanceof Set ? ids : new Set(ids)
    if (gone.has(scope.id)) await setScope(GLOBAL_SCOPE)
    else if (scope.kind === 'case' && !!scope.clientId && gone.has(scope.clientId)) {
      await setScope({ ...scope, clientId: null, clientName: null })
    }
  }

  /**
   * Case-rung convenience for the surfaces that only deal in cases. Callers pass the client when
   * they have it: a case rung built without one renders as a bare case name where the same rung
   * loaded from its row renders `Client › Case`.
   */
  async function setActiveCase(caseItem: ScopeCaseItem | null) {
    await setScope(caseItem
      ? { kind: 'case', id: caseItem.id, name: caseItem.name, clientId: caseItem.clientId ?? null, clientName: caseItem.clientName ?? null }
      : GLOBAL_SCOPE)
  }

  return {
    activeScope,
    activeCaseId,
    activeCaseName,
    activeClientId,
    setScope,
    setActiveCase,
    rememberConversationScope,
    adoptConversationScope,
    setKnownScopeIds,
    applyRememberedScope,
    clearScopeForDeleted,
  }
}
